import { say } from "../words.ts";
import { isUnboundCode, type ActionId } from "./controls/actions.ts";
import { pageKeys, type KeyTable } from "./controls/page.ts";
import { pageKeyHints } from "./touch/key-hints.ts";
import { readSetting, writeSetting, localStore, type SettingsStore } from "./settings.ts";

/**
 * The loading screen's tips (P1.9-T217): player-facing lines, one shown per load. A tip that names a key reads the
 * player's own binding from the key table, so a rebound key is the key it says; a tip whose key is unbound is not
 * offered. The choice is the client's alone, never repeats the tip shown on the load before, and is kept per device.
 */

/**
 * One tip: the action whose key it names, if any, and how it is worded.
 */
interface Tip {
  readonly action: ActionId | null;
  readonly keyboardOnly?: true;
  readonly words: (key: string) => string;
}

/**
 * The tips, in a fixed order the choice indexes into.
 */
const TIPS: readonly Tip[] = [
  { action: "run", words: (key) => say("loading.tips.sprint", { key }) },
  { action: "interact", words: (key) => say("loading.tips.interact", { key }) },
  { action: "inventory", words: (key) => say("loading.tips.bag", { key }) },
  { action: "worldMap", words: (key) => say("loading.tips.map", { key }) },
  { action: "target", words: (key) => say("loading.tips.target", { key }) },
  { action: "stance", words: (key) => say("loading.tips.stance", { key }) },
  { action: "itemNames", words: (key) => say("loading.tips.itemNames", { key }) },
  { action: "emotes", words: (key) => say("loading.tips.emotes", { key }) },
  { action: "openChat", words: (key) => say("loading.tips.chat", { key }) },
  { action: null, words: () => say("loading.tips.autoLoot") },
  { action: null, keyboardOnly: true, words: () => say("loading.tips.click") },
  { action: null, words: () => say("loading.tips.minimap") },
  { action: null, words: () => say("loading.tips.party") },
  { action: null, keyboardOnly: true, words: () => say("loading.tips.menu") },
  { action: null, words: () => say("loading.tips.zoom") },
];

/**
 * The storage key the index of the tip last shown is kept under.
 */
export const LAST_TIP_KEY = "odyssey.loading.tip";

/**
 * Reads the stored index of the last tip shown.
 *
 * @param raw The stored text, or null when nothing is stored.
 * @returns The index, or -1 for none or anything unreadable.
 */
function parseLastTip(raw: string | null): number {
  const index = raw === null ? Number.NaN : Number(raw);
  return Number.isInteger(index) && index >= 0 && index < TIPS.length ? index : -1;
}

/**
 * The indices of the tips that can be shown: those naming no key, and those whose key is bound; on a page with no
 * keyboard in use (`touch/key-hints.ts`) only the tips that name neither a key nor the mouse.
 *
 * @param keys The player's key table.
 * @param keyboard Whether a keyboard is in use; true unless given.
 * @returns The indices, in order.
 */
export function offeredTips(keys: Pick<KeyTable, "code">, keyboard = true): readonly number[] {
  return TIPS.flatMap((tip, index) => {
    if (tip.action === null) {
      return keyboard || tip.keyboardOnly !== true ? [index] : [];
    }
    return keyboard && !isUnboundCode(keys.code(tip.action)) ? [index] : [];
  });
}

/**
 * Chooses a tip, never the one shown last when another is offered.
 *
 * @param offered The indices that can be shown.
 * @param last The index shown on the load before, or -1.
 * @param random A source in [0, 1).
 * @returns The index chosen, or -1 when none is offered.
 */
export function chooseTip(offered: readonly number[], last: number, random: () => number): number {
  const fresh = offered.length > 1 ? offered.filter((index) => index !== last) : offered;
  return fresh.length === 0 ? -1 : (fresh[Math.min(fresh.length - 1, Math.floor(random() * fresh.length))] as number);
}

/**
 * The words of a tip.
 *
 * @param index The tip's index.
 * @param keys The player's key table, for the label of the key a tip names.
 * @returns The line.
 * @throws RangeError when there is no such tip.
 */
export function tipWords(index: number, keys: Pick<KeyTable, "label">): string {
  const tip = TIPS[index];
  if (tip === undefined) {
    throw new RangeError(`no loading tip ${index}`);
  }
  return tip.words(tip.action === null ? "" : keys.label(tip.action));
}

/**
 * Picks the tip for this load, and keeps which it was so the next load shows another.
 *
 * @param keys The player's key table, its keys and their labels; the page's unless given.
 * @param store Where the last tip is kept; this browser's unless given.
 * @param random A source in [0, 1); `Math.random` unless given.
 * @param keyboard Whether a keyboard is in use; the page's answer unless given.
 * @returns The line, or null when no tip is offered.
 */
export function pickLoadingTip(keys: Pick<KeyTable, "code" | "label"> = pageKeys, store: SettingsStore | null = localStore(), random: () => number = Math.random, keyboard: boolean = pageKeyHints.getSnapshot()): string | null {
  const index = chooseTip(offeredTips(keys, keyboard), readSetting(LAST_TIP_KEY, parseLastTip, store), random);
  if (index < 0) {
    return null;
  }
  writeSetting(LAST_TIP_KEY, String(index), store);
  return tipWords(index, keys);
}
